import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = process.env.REACT_APP_BACKEND_URL;

const pages = [
  { id: "home", label: "Home Page" },
  { id: "about", label: "About Page" },
  { id: "services", label: "Services Page" },
  { id: "booking", label: "Packages & FAQ" },
  { id: "contact", label: "Contact Page" },
];

const quillModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link", "image"],
    ["clean"],
  ],
};

export const AdminPageEditor = () => {
  const [page, setPage] = useState("home");
  const [content, setContent] = useState({ title: "", heroText: "", heroImage: "", sections: [] });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("desktop");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/pages/${page}`);
      setContent({ title: data.title || "", heroText: data.heroText || "", heroImage: data.heroImage || "", sections: data.sections || [] });
    } catch (err) { if (err?.response?.status !== 404) console.error("Load page:", err); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/api/admin/pages/${page}`, content); toast.success("Page saved!"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const currentPage = pages.find(p => p.id === page);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".75rem" }}>
        <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
          {pages.map(p => (
            <button key={p.id} onClick={() => setPage(p.id)} style={{
              padding: ".4rem .8rem", borderRadius: 6, border: "none", fontSize: ".8rem", fontWeight: 500, cursor: "pointer",
              background: page === p.id ? "#1F2937" : "#F3F4F6", color: page === p.id ? "#fff" : "#6B7280",
            }}>{p.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
          {/* Preview Size */}
          <div style={{ display: "flex", border: "1px solid #D1D5DB", borderRadius: 6, overflow: "hidden" }}>
            {[
              { id: "desktop", icon: "fa-desktop" },
              { id: "tablet", icon: "fa-tablet-alt" },
              { id: "mobile", icon: "fa-mobile-alt" },
            ].map(d => (
              <button key={d.id} onClick={() => setPreview(d.id)} style={{
                padding: ".35rem .6rem", border: "none", cursor: "pointer", fontSize: ".75rem",
                background: preview === d.id ? "#1F2937" : "#fff", color: preview === d.id ? "#fff" : "#6B7280",
              }}>
                <i className={`fas ${d.icon}`} />
              </button>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="page-save-button" style={{
            padding: ".5rem 1.25rem", borderRadius: 8, border: "none",
            background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".82rem", fontWeight: 600,
          }}>
            {saving ? "Saving..." : "Save Page"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1.5rem" }}>
        {/* Main Editor */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1.25rem" }}>
            <i className="fas fa-file-alt" style={{ color: "#EC4899", marginRight: ".5rem" }} />
            Editing: {currentPage?.label}
          </h3>

          <div style={{ marginBottom: "1.25rem" }}>
            <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".35rem" }}>Page Title</label>
            <input value={content.title} onChange={e => setContent(p => ({ ...p, title: e.target.value }))} placeholder="Page title" data-testid="page-title-input"
              style={{ width: "100%", padding: ".6rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".95rem", fontWeight: 600 }} />
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".35rem" }}>Hero / Banner Content</label>
            <ReactQuill theme="snow" value={content.heroText} onChange={v => setContent(p => ({ ...p, heroText: v }))} modules={quillModules} style={{ borderRadius: 8 }} />
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".35rem" }}>Hero Image URL</label>
            <input value={content.heroImage} onChange={e => setContent(p => ({ ...p, heroImage: e.target.value }))} placeholder="https://..." data-testid="page-image-input"
              style={{ width: "100%", padding: ".55rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
          </div>
        </div>

        {/* Preview Sidebar */}
        <div>
          {/* Image Preview */}
          {content.heroImage && (
            <div style={{ background: "#fff", borderRadius: 12, padding: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1rem" }}>
              <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .6rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Image Preview</h4>
              <img src={content.heroImage} alt="Hero" style={{ width: "100%", borderRadius: 8, maxHeight: 160, objectFit: "cover" }} />
            </div>
          )}

          {/* Page Info */}
          <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1rem" }}>
            <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Page Info</h4>
            <div style={{ fontSize: ".82rem", color: "#6B7280" }}>
              <p style={{ margin: "0 0 .4rem" }}><strong>Route:</strong> /{(() => { if (page === "home") return ""; if (page === "booking") return "book"; return page; })()}</p>
              <p style={{ margin: "0 0 .4rem" }}><strong>Status:</strong> <span style={{ color: "#10B981" }}>Published</span></p>
            </div>
          </div>

          {/* Component Library */}
          <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
            <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Components</h4>
            <p style={{ fontSize: ".78rem", color: "#9CA3AF", marginBottom: ".6rem" }}>Available elements for this page</p>
            {[
              { icon: "fa-heading", name: "Heading" },
              { icon: "fa-align-left", name: "Paragraph" },
              { icon: "fa-image", name: "Image" },
              { icon: "fa-hand-pointer", name: "CTA Button" },
              { icon: "fa-quote-right", name: "Testimonial" },
              { icon: "fa-envelope", name: "Contact Form" },
              { icon: "fa-th-large", name: "Feature Grid" },
              { icon: "fa-video", name: "Video Embed" },
            ].map(c => (
              <div key={c.name} style={{
                display: "flex", alignItems: "center", gap: ".5rem", padding: ".45rem .6rem",
                borderRadius: 6, cursor: "grab", fontSize: ".82rem", color: "#374151", marginBottom: ".25rem",
                border: "1px dashed #E5E7EB", transition: "all .15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#EC4899"; e.currentTarget.style.background = "rgba(236,72,153,.03)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.background = "transparent"; }}
              >
                <i className={`fas ${c.icon}`} style={{ width: 16, textAlign: "center", color: "#EC4899", fontSize: ".75rem" }} />
                {c.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
